/**
 * Tenant Context
 * Manages tenant-specific features, license info, and edition-based access control
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const TenantContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default features for platform users (all enabled)
const PLATFORM_FEATURES = {
  ai_course_generation: true,
  ai_translation: true,
  ai_recommendations: true,
  ai_moderation: true,
  hr_integration: true,
  whatsapp_integration: true,
  advanced_analytics: true,
  ai_features: true,
};

// Features by edition
const EDITION_FEATURES = {
  standard: {
    ai_course_generation: false,
    ai_translation: false,
    ai_recommendations: false,
    ai_moderation: false,
    hr_integration: false,
    whatsapp_integration: false,
    advanced_analytics: false,
    ai_features: false,
  },
  professional: {
    ai_course_generation: false,
    ai_translation: true,
    ai_recommendations: true,
    ai_moderation: false,
    hr_integration: true,
    whatsapp_integration: true,
    advanced_analytics: false,
    ai_features: true,
  },
  enterprise: {
    ai_course_generation: true,
    ai_translation: true,
    ai_recommendations: true,
    ai_moderation: true,
    hr_integration: true,
    whatsapp_integration: true,
    advanced_analytics: true,
    ai_features: true,
  },
};

export const TenantProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [tenantInfo, setTenantInfo] = useState(null);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [features, setFeatures] = useState(PLATFORM_FEATURES);
  const [edition, setEdition] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch tenant info when user logs in
  const fetchTenantInfo = useCallback(async () => {
    if (!token || !user) {
      setLoading(false);
      return;
    }

    try {
      // First check if user has tenant_id
      const tenantId = user.tenant_id;
      
      if (!tenantId) {
        // Platform user - all features enabled
        setFeatures(PLATFORM_FEATURES);
        setEdition('platform');
        setLicenseInfo(null);
        setLoading(false);
        return;
      }

      // Fetch license usage for tenant users
      const licenseRes = await axios.get(`${API_URL}/oem/tenants/${tenantId}/license-usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const licenseData = licenseRes.data;
      setLicenseInfo(licenseData);
      setEdition(licenseData.edition || 'standard');
      
      // Set features based on edition
      const editionFeatures = EDITION_FEATURES[licenseData.edition] || EDITION_FEATURES.standard;
      
      // Also check if tenant has custom features override
      if (licenseData.features) {
        setFeatures({ ...editionFeatures, ...licenseData.features });
      } else {
        setFeatures(editionFeatures);
      }
      
      setTenantInfo({
        id: tenantId,
        edition: licenseData.edition,
        company_name: licenseData.company_name,
      });
      
    } catch (error) {
      console.log('Tenant info not available - using platform defaults');
      // If API fails, assume platform user
      setFeatures(PLATFORM_FEATURES);
      setEdition('platform');
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    fetchTenantInfo();
  }, [fetchTenantInfo]);

  // Check if a specific feature is enabled
  const hasFeature = (featureName) => {
    return features[featureName] === true;
  };

  // Check if any AI feature is enabled
  const hasAnyAIFeature = () => {
    return features.ai_features === true || 
           features.ai_course_generation === true || 
           features.ai_translation === true ||
           features.ai_recommendations === true;
  };

  // Check if user can add more users based on license
  const canAddUser = (role) => {
    if (!licenseInfo) return true; // Platform users have no limit
    
    const isCreatorRole = ['admin', 'training_team'].includes(role);
    
    if (isCreatorRole) {
      return licenseInfo.can_add_creator !== false;
    } else {
      return licenseInfo.can_add_viewer !== false;
    }
  };

  // Get license limit message
  const getLicenseLimitMessage = (role) => {
    if (!licenseInfo) return null;
    
    const isCreatorRole = ['admin', 'training_team'].includes(role);
    const license = licenseInfo.license;
    
    if (!license) return null;
    
    if (isCreatorRole && licenseInfo.can_add_creator === false) {
      return `Creator license limit reached (${license.active_creators}/${license.creator_licenses}). Contact sales to add more licenses.`;
    }
    if (!isCreatorRole && licenseInfo.can_add_viewer === false) {
      return `Viewer license limit reached (${license.active_viewers}/${license.viewer_licenses}). Contact sales to add more licenses.`;
    }
    return null;
  };

  // Refresh license info (call after adding/removing users)
  const refreshLicenseInfo = async () => {
    await fetchTenantInfo();
  };

  return (
    <TenantContext.Provider value={{
      tenantInfo,
      licenseInfo,
      features,
      edition,
      loading,
      hasFeature,
      hasAnyAIFeature,
      canAddUser,
      getLicenseLimitMessage,
      refreshLicenseInfo,
      isPlatformUser: !user?.tenant_id,
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export default TenantContext;
